import { createClient, type Session, type User } from '@supabase/supabase-js';
import type { AuthService, AuthSession, AuthStateListener, AuthUser } from './types';

const DEFAULT_ERROR_MESSAGE = 'Unable to complete the request.';

export interface SupabaseLikeUser {
  readonly id: string;
  readonly email?: string | null;
  readonly identities?: readonly unknown[] | null;
}

export interface SupabaseLikeSession {
  readonly user: SupabaseLikeUser;
  readonly expires_at?: number | null;
}

export interface SupabaseLikeClient {
  readonly rpc?: (
    functionName: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message?: string; code?: string } | null }>;
  readonly auth: {
    signUp: (args: { email: string; password: string; options?: { data?: Record<string, string> } }) => Promise<{ data: { user?: SupabaseLikeUser | null; session?: SupabaseLikeSession | null }; error: { message?: string } | null }>;
    signInWithPassword: (args: { email: string; password: string }) => Promise<{ data: { session?: SupabaseLikeSession | null }; error: { message?: string } | null }>;
    signOut: () => Promise<{ error: { message?: string } | null }>;
    getSession: () => Promise<{ data: { session?: SupabaseLikeSession | null }; error: { message?: string } | null }>;
    getUser: () => Promise<{ data: { user?: SupabaseLikeUser | null }; error: { message?: string } | null }>;
    resetPasswordForEmail: (email: string, options?: { redirectTo?: string }) => Promise<{ error: { message?: string } | null }>;
    updateUser: (args: { password: string }) => Promise<{ error: { message?: string } | null }>;
    refreshSession: () => Promise<{ data: { session?: SupabaseLikeSession | null }; error: { message?: string } | null }>;
    onAuthStateChange: (
      callback: (event: string, session: SupabaseLikeSession | null) => void,
    ) => { data: { subscription: { unsubscribe: () => void } } };
  };
}

export interface SupabaseSyncClient extends SupabaseLikeClient {
  readonly rpc: NonNullable<SupabaseLikeClient['rpc']>;
}

function toUser(value: User | SupabaseLikeUser | null): AuthUser | null {
  if (!value) return null;
  return { id: value.id, email: value.email ?? null };
}

function toSession(value: Session | SupabaseLikeSession | null): AuthSession | null {
  if (!value) return null;
  return {
    userId: value.user.id,
    email: value.user.email ?? null,
    expiresAt: value.expires_at ? Math.floor(value.expires_at) : null,
  };
}

export function mapSupabaseError(error: { message?: string } | Error | null | undefined): string {
  if (!error) return DEFAULT_ERROR_MESSAGE;

  const message = 'message' in error ? error.message ?? DEFAULT_ERROR_MESSAGE : DEFAULT_ERROR_MESSAGE;
  const lowered = message.toLowerCase();

  if (lowered.includes('invalid login credentials') || lowered.includes('invalid email') || lowered.includes('invalid password')) {
    return 'Invalid email or password.';
  }

  if (lowered.includes('email not confirmed') || lowered.includes('not confirmed') || lowered.includes('confirm your email')) {
    return 'Please check your email and verify your account before signing in.';
  }

  if (lowered.includes('user already registered') || lowered.includes('already exists') || lowered.includes('already in use')) {
    return 'An account with this email already exists.';
  }

  if (
    lowered.includes('password') &&
    (lowered.includes('weak') ||
      lowered.includes('too short') ||
      lowered.includes('length') ||
      lowered.includes('at least 8') ||
      lowered.includes('should be at least') ||
      lowered.includes('must be at least'))
  ) {
    return 'Password is too weak. Use at least 8 characters.';
  }

  if (lowered.includes('reset') || lowered.includes('recovery')) {
    return 'Password reset email could not be sent.';
  }

  if (lowered.includes('session') || lowered.includes('expired')) {
    return 'Your session has expired. Please sign in again.';
  }

  if (lowered.includes('network') || lowered.includes('fetch')) {
    return 'Network issue detected. Please try again.';
  }

  return message;
}

let sharedSupabaseClient: SupabaseLikeClient | null = null;

function getSupabaseClient(): SupabaseLikeClient {
  if (sharedSupabaseClient) {
    return sharedSupabaseClient;
  }

  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    throw new Error('Account service is not configured. The VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables must be set.');
  }

  sharedSupabaseClient = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }) as unknown as SupabaseLikeClient;

  return sharedSupabaseClient;
}

export function getSupabaseSyncClient(): SupabaseSyncClient {
  const client = getSupabaseClient();
  if (!client.rpc) throw new Error('Cloud sync is unavailable in this client.');
  return client as SupabaseSyncClient;
}

export function createSupabaseAuthService(getClient: () => SupabaseLikeClient = getSupabaseClient): AuthService {
  let refreshInProgress = false;
  let refreshPromise: Promise<AuthSession | null> | null = null;

  async function getRefreshedSession(): Promise<AuthSession | null> {
    if (refreshInProgress && refreshPromise) {
      return refreshPromise;
    }

    refreshInProgress = true;
    refreshPromise = (async () => {
      try {
        const client = getClient();
        const { data, error } = await client.auth.refreshSession();
        if (error) {
          console.warn('[auth] Session refresh failed:', mapSupabaseError(error));
          return null;
        }
        return toSession(data.session ?? null);
      } catch (error) {
        console.warn('[auth] Session refresh exception:', error);
        return null;
      } finally {
        refreshInProgress = false;
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }

  return {
    async signUp(email: string, password: string, fullName?: string) {
      const client = getClient();
      const { data, error } = await client.auth.signUp({ email, password, options: fullName ? { data: { full_name: fullName } } : undefined });

      if (error) {
        throw new Error(mapSupabaseError(error));
      }

      if (data.user && (!data.user.identities || data.user.identities.length === 0) && !data.session) {
        throw new Error('An account with this email already exists. Sign in instead.');
      }

      const userId = data.user?.id;
      const requiresEmailConfirmation = Boolean(data.user && !data.session);

      return { userId, requiresEmailConfirmation };
    },

    async signIn(email, password) {
      const client = getClient();
      const { data, error } = await client.auth.signInWithPassword({ email, password });

      if (error) {
        throw new Error(mapSupabaseError(error));
      }

      const session = toSession(data.session ?? null);
      if (!session) {
        throw new Error('Unable to sign in.');
      }

      return session;
    },

    async signOut() {
      const client = getClient();
      const { error } = await client.auth.signOut();
      if (error) throw new Error(mapSupabaseError(error));
    },

    async getCurrentSession() {
      const client = getClient();
      const { data, error } = await client.auth.getSession();
      if (error) {
        // If session retrieval failed, try to refresh
        console.warn('[auth] getSession failed, attempting refresh:', mapSupabaseError(error));
        const refreshed = await getRefreshedSession();
        if (refreshed) return refreshed;
        throw new Error(mapSupabaseError(error));
      }
      return toSession(data.session ?? null);
    },

    async getCurrentUser() {
      const client = getClient();
      const { data, error } = await client.auth.getUser();
      if (error) {
        throw new Error(mapSupabaseError(error));
      }
      return toUser(data.user ?? null);
    },

    async requestPasswordReset(email) {
      const client = getClient();
      const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/#/reset-password` : undefined;
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });

      if (error) {
        throw new Error(mapSupabaseError(error));
      }
    },

    async confirmPasswordReset(password: string) {
      const client = getClient();
      const { error } = await client.auth.updateUser({ password });
      if (error) {
        throw new Error(mapSupabaseError(error));
      }
    },

    async updatePassword(password) {
      const client = getClient();
      const { error } = await client.auth.updateUser({ password });

      if (error) {
        throw new Error(mapSupabaseError(error));
      }
    },

    onAuthStateChange(listener: AuthStateListener) {
      const client = getClient();
      const { data } = client.auth.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          listener('recovering', null);
          return;
        }

        if (event === 'TOKEN_REFRESHED' && session) {
          // Token was refreshed successfully - notify with new session
          const nextUser = toUser(session.user ?? null);
          listener('authenticated', nextUser);
          return;
        }

        if (event === 'SIGNED_OUT') {
          listener('unauthenticated', null);
          return;
        }

        const nextUser = toUser(session?.user ?? null);
        const state = session ? 'authenticated' : event === 'INITIAL_SESSION' ? 'loading' : 'unauthenticated';
        listener(state, nextUser);
      });

      return () => {
        data.subscription.unsubscribe();
      };
    },
  };
}

export const supabaseAuthService = createSupabaseAuthService();
